import React, { useContext, useState } from "react";

import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { FormTranslationsContext } from "../../contexts/FormTranslationsContext";
import ConfirmModal from "../modal/ConfirmModal";

import type { JSX } from "react";

import type { Translation } from "./FormPictures";

interface DeletePicturesButtonProps {
  onDelete: ({ translationId }: { translationId: string }) => Promise<void>;
}

export default function DeletePicturesButton({
  onDelete,
}: DeletePicturesButtonProps): JSX.Element | null {
  const { t } = useTranslation("form");
  const { activeLanguageIndex, methods } = useContext(FormTranslationsContext);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const translation = useWatch({
    control: methods?.control,
    name: `translations.${activeLanguageIndex ?? 0}`,
  }) as unknown as Translation | undefined;

  const picture = translation?.picture ?? null;
  const hasPictures =
    picture != null && Object.values(picture).some((entry) => entry?.url);

  if (!translation || !hasPictures) return null;

  const handleConfirm = async (): Promise<void> => {
    await onDelete({ translationId: translation.uuid });
    setIsModalOpen(false);
  };

  return (
    <React.Fragment>
      <div
        className="bg-destructive/10 text-destructive hover:bg-destructive/20 hover:text-destructive/80 flex cursor-pointer items-center rounded-[0.15625rem] px-[0.3125rem] py-[0.15625rem] text-center text-[0.78125rem] font-medium transition-colors"
        onClick={(): void => setIsModalOpen(true)}
      >
        {t("deletePictures")}
      </div>
      <ConfirmModal
        isOpen={isModalOpen}
        onClose={(): void => setIsModalOpen(false)}
        title={t("modal.deletePictures.title")}
        actions={[
          {
            label: t("modal.deletePictures.confirm"),
            onClick: handleConfirm,
            buttonVariant: "delete",
          },
          {
            label: t("modal.deletePictures.cancel"),
            onClick: (): void => setIsModalOpen(false),
          },
        ]}
      >
        <p>{t("modal.deletePictures.body")}</p>
      </ConfirmModal>
    </React.Fragment>
  );
}
